// Timezone-safe handling of DATE-only values ("YYYY-MM-DD" as returned by the
// API for calendar dates like purchaseDate / nextActionDate).
//
// Parsing such a string with `new Date("2026-07-02")` treats it as UTC midnight,
// so `toLocaleDateString()` in a timezone behind UTC renders the *previous* day
// — the #16 off-by-one where the grid showed 7/1 for a stored 7/2. These helpers
// work on the raw year/month/day components and never cross a timezone boundary,
// so a date-only value always displays and computes as its true calendar date.

const YMD = /^(\d{4})-(\d{2})-(\d{2})/;

// Display a date value as M/D/YYYY. Date-only strings are formatted from their
// literal components (no timezone shift); full timestamps fall back to locale
// formatting, which is correct for datetime fields like createdAt/updatedAt.
export function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  const m = YMD.exec(String(v));
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

// Shift a date-only value by whole days/months, returning "YYYY-MM-DD". Uses UTC
// arithmetic so it never rolls across a timezone boundary.
function shift(iso: string | null, mutate: (d: Date) => void): string | null {
  const m = YMD.exec(iso ?? '');
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  mutate(d);
  return d.toISOString().slice(0, 10);
}
export const addDays = (iso: string | null, days: number): string | null =>
  shift(iso, d => d.setUTCDate(d.getUTCDate() + days));
export const addMonths = (iso: string | null, months: number): string | null =>
  shift(iso, d => d.setUTCMonth(d.getUTCMonth() + months));

// Is a date-only value strictly before today, comparing calendar dates only
// (both sides reduced to the local "YYYY-MM-DD", so no time-of-day/tz effects)?
export function isPastDate(iso: string | null): boolean {
  const m = YMD.exec(iso ?? '');
  if (!m) return false;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return `${m[1]}-${m[2]}-${m[3]}` < today;
}
